import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Clients from '../Clients';
import * as Objects from '../Objects';
import * as Stores from '../Stores';
import { Streams } from '..';

const __moduleName__ = `omniglot-live-data.Properties`;

export type TypeParams<BindingType extends Objects.BindingType = any, ValueType = any, DomainType extends Elevated.Domain<ValueType> = Elevated.Domain<ValueType>> = {
    Binding: BindingType,
    Value: ValueType,
    Domain: DomainType 
};

export interface Builder<BuilderTypeParams extends TypeParams = TypeParams> {
    object(symbolName: keyof BuilderTypeParams["Binding"]): void;
    measure(domain: BuilderTypeParams["Domain"]): void;
    example(maker: () => BuilderTypeParams["Value"]): void;

    scalar(): void;
    set(): void;
    sequence(): void;
    map<KeyType>(makeKey: () => KeyType): void;
}

export abstract class Descriptor<DescriptorTypeParams extends TypeParams = TypeParams> {
    abstract canonicalName: string;
    abstract build(builder: Builder<DescriptorTypeParams>): void;

    static Directory = new Elevated.Directory<Descriptor<any>>(`${__moduleName__}.Descriptor`);
    getDomain(): DescriptorTypeParams["Domain"] {
        let result: DescriptorTypeParams["Domain"]|undefined;
        this.build(new class {
            object(symbolName: keyof DescriptorTypeParams["Binding"]): void {}
            measure(domain: DescriptorTypeParams["Domain"]): void { result = domain }
            example(maker: () => DescriptorTypeParams["Value"]): void {}
        
            scalar(): void {}
            set(): void {}
            sequence(): void {}
            map<KeyType>(makeKey: () => KeyType): void {}
        })
        if(result === undefined)
            throw new Error('Malformed Properties.Descriptor did not call  Builder.measure, which is mandatory')
        return result;
    }
    bind(
        binding: DescriptorTypeParams["Binding"]
    ): Instance<DescriptorTypeParams> {
        if(Object.getOwnPropertyNames(binding).includes('binding')) {
            throw new Error(`Nested binding is illegal ${JSON.stringify(binding)}`)
        }
        return { descriptor: this, binding }
    }
    stream(
        zone: Stores.Zone,
        binding: DescriptorTypeParams["Binding"]
    ): Streams.PropertyStream<DescriptorTypeParams> {
        return zone.streams().property(this.bind(binding));
    }
    eager(zone: Stores.Zone, binding: DescriptorTypeParams["Binding"]) {
        return {
            scalar: () => {
                const stream = this.stream(zone, binding).scalar;
                if(!stream) return undefined;
                return Clients.ScalarClient.Eager<DescriptorTypeParams["Value"]>(this.canonicalName).upstream(stream);
            }, set: () => {
                const stream = this.stream(zone, binding).set;
                if(!stream) return undefined;
                return Clients.SetClient.Eager<DescriptorTypeParams["Value"]>(this.canonicalName).upstream(stream);
            }, sequence: () => {
                const stream = this.stream(zone, binding).sequence;
                if(!stream) return undefined;
                return Clients.SequenceClient.Eager<DescriptorTypeParams["Value"]>(this.canonicalName).upstream(stream);
            }, map: () => {
                const stream = this.stream(zone, binding).map;
                if(!stream) return undefined;
                return Clients.MapClient.Eager<any, DescriptorTypeParams["Value"]>(this.canonicalName).upstream(stream);
            }
        };
    }
    getSymbols(): (keyof DescriptorTypeParams["Binding"])[] {
        const symbols: (keyof DescriptorTypeParams["Binding"])[] = [];
        this.build(new class {
            object(symbolName: keyof DescriptorTypeParams["Binding"]): void { symbols.push(symbolName) }
            measure(domain: DescriptorTypeParams["Domain"]): void { }
            example(maker: () => DescriptorTypeParams["Value"]): void {}

            scalar(): void {}
            set(): void {}
            sequence(): void {}
            map<KeyType>(makeKey: () => KeyType): void {}
        });
        return symbols;
    }
}

export interface Instance<ContextTypeParams extends TypeParams = TypeParams> {
    descriptor: Descriptor<ContextTypeParams>;
    binding: ContextTypeParams["Binding"];
}
